import { createFileRoute } from '@tanstack/react-router';
import { useState, lazy, Suspense } from 'react';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { Share2 } from 'lucide-react';
import { Button } from '@/components/ui/button';

import { useProductBySlug, productBySlugQueryOptions } from '@/hooks/productHooks';
import ImageSlider from '@/components/ImageSlider';
import StoreCardHorizontal from '@/components/product/StoreCardHorizontal';
import SimilarProducts from '@/components/SimilarProducts';
import SimilarStores from '@/components/SimilarStores';
import MoreFromStore from '@/components/MoreFromStore';
import LoveButton from '@/components/LoveButton';
import SaveButton from '@/components/SaveButton';

// Lazy load ShareDialog – only loaded when needed
const ShareDialog = lazy(() => import('@/components/ShareDialog'));

export const Route = createFileRoute('/_regular/product/$productSlug')({
  component: ProductPage,
  loader: ({ context: { queryClient }, params }) =>
    queryClient
      .ensureQueryData(productBySlugQueryOptions(params.productSlug))
      .catch(() => null),
  head: ({ loaderData: product }) => {
    if (!product) return {}
    const description = product.description?.slice(0, 160)
    const image = product.images?.[0]?.url
    return {
      meta: [
        { title: `${product.name} | سكيكيمة` },
        description && { name: 'description', content: description },
        { property: 'og:type', content: 'product' },
        { property: 'og:title', content: product.name },
        description && { property: 'og:description', content: description },
        image && { property: 'og:image', content: image },
      ].filter(Boolean),
    }
  },
});

function ProductPage() {
  const { productSlug } = Route.useParams();
  const { data: product, isLoading, error } = useProductBySlug(productSlug);

  const [shareModalOpen, setShareModalOpen] = useState(false);

  const store = product?.store;
  const cityId = store?.city?.id;
  const storeTypeId = store?.store_type?.id;
  const categoryId = product?.category?.id;

  const productUrl = typeof window !== 'undefined' ? window.location.href : '';

  if (isLoading) return <ProductPageSkeleton />;
  if (error || !product)
    return <div className="p-6 text-center text-red-500">حدث خطأ</div>;

  return (
    <div className="container mx-auto p-4 md:p-6 space-y-8" dir="rtl">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Right column */}
        <div>
          <StoreCardHorizontal store={store} />
          <ImageSlider images={product.images} productName={product.name} />
        </div>

        {/* Left column */}
        <div className="space-y-6">
          <div className="bg-card rounded-lg border p-6 space-y-4">
            <h1 className="text-2xl font-bold">{product.name}</h1>

            <div className="flex items-center gap-4">
              <Badge variant="secondary">{product.category?.name}</Badge>
              {product.catalogue && (
                <Badge variant="outline">{product.catalogue.name}</Badge>
              )}
            </div>

            {product.description && (
              <p className="text-muted-foreground text-sm leading-relaxed whitespace-pre-line">
                {product.description}
              </p>
            )}

            <div className="flex items-baseline gap-2">
              <span className="text-3xl font-bold text-primary">
                {new Intl.NumberFormat('ar-MA', {
                  style: 'currency',
                  currency: 'MAD',
                }).format(product.price)}
              </span>

              {product.compare_at_price && (
                <span className="text-lg text-muted-foreground line-through">
                  {new Intl.NumberFormat('ar-MA', {
                    style: 'currency',
                    currency: 'MAD',
                  }).format(product.compare_at_price)}
                </span>
              )}
            </div>

            <div className="flex items-center justify-between border-t pt-3 mt-3">
              <div className="flex items-center gap-4">
                <LoveButton
                  productId={product.id}
                  initialLoved={product.is_loved}
                  initialCount={product.loves_count}
                  size="sm"
                  className="p-0"
                />

                <SaveButton
                  productId={product.id}
                  initialSaved={product.is_saved}
                  size="sm"
                  className="p-0"
                />
              </div>

              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShareModalOpen(true)}
              >
                <Share2 className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Specifications */}
          {product.specifications?.length > 0 && (
            <div className="bg-card rounded-lg border p-6">
              <h2 className="text-xl font-semibold mb-4">المواصفات</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {product.specifications.map((item) => {
                  let displayValue = item.value;
                  if (Array.isArray(displayValue)) {
                    displayValue = displayValue.join('، ');
                  } else if (item.specification.type === 'boolean') {
                    displayValue = item.value ? 'نعم' : 'لا';
                  }
                  return (
                    <div key={item.id} className="flex justify-between border-b pb-2">
                      <span className="text-muted-foreground">{item.specification.name}</span>
                      <span className="font-medium">{displayValue}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      <Separator />

      {cityId && storeTypeId && categoryId && (
        <SimilarProducts
          cityId={cityId}
          storeTypeId={storeTypeId}
          categoryId={categoryId}
          excludeProductId={product.id}
        />
      )}

      {cityId && storeTypeId && (
        <SimilarStores
          cityId={cityId}
          storeTypeId={storeTypeId}
          excludeStoreId={store.id}
        />
      )}

      {store && (
        <MoreFromStore storeSlug={store.slug} excludeProductId={product.id} />
      )}

      {/* Lazy‑loaded ShareDialog */}
      {shareModalOpen && (
        <Suspense fallback={null}>
          <ShareDialog
            open={shareModalOpen}
            onOpenChange={setShareModalOpen}
            url={productUrl}
            title={product.name}
            type="product"
          />
        </Suspense>
      )}
    </div>
  );
}

function ProductPageSkeleton() {
  return (
    <div className="container mx-auto p-4 md:p-6 space-y-8" dir="rtl">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="space-y-4">
          <Skeleton className="h-24 w-full rounded-lg" />
          <Skeleton className="h-64 w-full rounded-lg" />
        </div>
        <div className="space-y-6">
          <Skeleton className="h-80 w-full rounded-lg" />
          <Skeleton className="h-48 w-full rounded-lg" />
        </div>
      </div>
    </div>
  );
}